import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, User, ArrowLeft, Image as ImageIcon } from 'lucide-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';

const EventoDetalle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [evento, setEvento] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvento = async () => {
      setLoading(true);
      try {
        const response = await apiService.getEventoDetalle(slug);
        if (response.estado === 1) {
          setEvento(response.data);
        }
      } catch (error) {
        console.error('Error cargando detalle del evento:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchEvento();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('es-PE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center pt-20">
        <motion.div 
          className="w-16 h-16 border-4 border-yellow-600/30 border-t-yellow-500 rounded-full" 
          animate={{ rotate: 360 }} 
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }} 
        />
      </div>
    );
  }

  if (!evento) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center pt-20 text-center px-4">
        <h2 className="text-3xl font-bold text-white mb-4">Evento no encontrado</h2>
        <Link to="/eventos" className="text-yellow-500 hover:underline flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Volver a eventos
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Botón Volver */}
        <motion.button 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-gray-400 hover:text-yellow-500 transition-colors mb-8 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span>Volver a eventos</span>
        </motion.button>

        {/* Portada del Evento */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative w-full h-[400px] md:h-[500px] lg:h-[600px] rounded-2xl overflow-hidden border border-yellow-600/20 mb-12 bg-neutral-900/30"
        >
          <img 
            src={evento.imagen_portada_url || evento.imagen_portada} 
            alt={evento.titulo} 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
          
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight"
            >
              {evento.titulo}
            </motion.h1>
            
            <div className="flex flex-wrap gap-4 md:gap-6 text-sm md:text-base text-gray-300">
              {evento.fecha_evento && (
                <div className="flex items-center gap-2 bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full border border-yellow-600/30">
                  <Calendar className="w-4 h-4 text-yellow-500" />
                  {formatDate(evento.fecha_evento)}
                </div>
              )}
              {evento.ubicacion && (
                <div className="flex items-center gap-2 bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full border border-yellow-600/30">
                  <MapPin className="w-4 h-4 text-yellow-500" />
                  {evento.ubicacion}
                </div>
              )}
              {evento.cliente && (
                <div className="flex items-center gap-2 bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full border border-yellow-600/30">
                  <User className="w-4 h-4 text-yellow-500" />
                  {evento.cliente}
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Contenido y Galería */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          
          {/* Columna Principal: Descripción */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:col-span-2"
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-yellow-500 rounded-full"></span>
              Sobre el Evento
            </h2>
            <div 
              className="prose prose-invert prose-lg max-w-none text-gray-300 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: evento.contenido || evento.resumen }}
            />
          </motion.div>

          {/* Columna Lateral: Galería del Evento */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="lg:col-span-1"
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-yellow-500 rounded-full"></span>
              Galería
            </h2>
            
            {evento.galeria && evento.galeria.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {evento.galeria.map((img, idx) => (
                  <motion.div
                    key={img.id || idx}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 + idx * 0.1 }}
                    className="relative aspect-square rounded-xl overflow-hidden border border-yellow-600/20 group cursor-pointer bg-neutral-900"
                  >
                    <img 
                      src={img.url_completa || img.url_imagen} 
                      alt={img.alt_texto || evento.titulo} 
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                      <ImageIcon className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="bg-neutral-900/30 border border-yellow-600/20 rounded-xl p-8 text-center">
                <ImageIcon className="w-12 h-12 text-yellow-600/40 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">No hay imágenes adicionales para este evento.</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Call to Action */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-16 text-center bg-neutral-900/40 border border-yellow-600/20 rounded-2xl p-8 md:p-12"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">¿Quieres producir un evento como este?</h3>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Contáctanos hoy mismo y hagamos realidad tu próximo proyecto con la mejor tecnología y equipo humano.
          </p>
          <Link 
            to="/contacto" 
            className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black font-bold px-8 py-4 rounded-full hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-lg hover:shadow-yellow-500/25"
          >
            Solicitar Cotización
            <ArrowLeft className="w-4 h-4 rotate-180" />
          </Link>
        </motion.div>

      </div>
    </div>
  );
};

export default EventoDetalle;